import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useBlockingMutation } from '../hooks/useBlockingMutation';
import { Window, Button, TextField, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '@warmancer/ui';
import { accountsApi, moderationApi, ApiClientError } from '../api/client';
import { useAdminStore } from '../stores/adminStore';
import { formatTimestamp, formatDate } from '../utils/formatTimestamp';
import styles from './AccountDetail.module.css';

export function AccountDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasRole } = useAdminStore();

  const [showBanModal, setShowBanModal] = useState(false);
  const [showMuteModal, setShowMuteModal] = useState(false);
  const [showWarnModal, setShowWarnModal] = useState(false);
  const [showFangsModal, setShowFangsModal] = useState(false);
  const [showClearDevicesConfirm, setShowClearDevicesConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showChangeEmailModal, setShowChangeEmailModal] = useState(false);
  const [showChangeUsernameModal, setShowChangeUsernameModal] = useState(false);
  const [error, setError] = useState('');

  const openModal = (setter: (v: boolean) => void) => {
    setError('');
    setter(true);
  };

  const closeModal = (setter: (v: boolean) => void) => {
    setError('');
    setter(false);
  };

  const { data, isLoading } = useQuery({
    queryKey: ['account', id],
    queryFn: () => accountsApi.get(id!),
    enabled: !!id,
  });

  const { data: devicesData, isLoading: devicesLoading } = useQuery({
    queryKey: ['account-devices', id],
    queryFn: () => accountsApi.getDevices(id!),
    enabled: !!id,
  });

  const clearDevicesMutation = useBlockingMutation({
    loadingMessage: 'Clearing devices...',
    mutationFn: () => accountsApi.clearDevices(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-devices', id] });
      closeModal(setShowClearDevicesConfirm);
    },
    onError: (err) => {
      setError(err instanceof ApiClientError ? err.message : 'Failed to clear devices');
    },
  });

  const account = data?.account;
  const moderationHistory = data?.moderation_history || [];
  const devices = devicesData?.devices || [];

  const banMutation = useBlockingMutation({
    loadingMessage: 'Banning account...',
    mutationFn: (data: { reason: string; duration_minutes?: number }) =>
      moderationApi.ban(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account', id] });
      closeModal(setShowBanModal);
    },
    onError: (err) => {
      setError(err instanceof ApiClientError ? err.message : 'Failed to ban account');
    },
  });

  const unbanMutation = useBlockingMutation({
    loadingMessage: 'Unbanning account...',
    mutationFn: () => moderationApi.unban(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account', id] });
    },
  });

  const muteMutation = useBlockingMutation({
    loadingMessage: 'Muting account...',
    mutationFn: (data: { reason: string; duration_minutes: number }) =>
      moderationApi.mute(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account', id] });
      closeModal(setShowMuteModal);
    },
    onError: (err) => {
      setError(err instanceof ApiClientError ? err.message : 'Failed to mute account');
    },
  });

  const warnMutation = useBlockingMutation({
    loadingMessage: 'Issuing warning...',
    mutationFn: (data: { reason: string }) => moderationApi.warn(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account', id] });
      closeModal(setShowWarnModal);
    },
    onError: (err) => {
      setError(err instanceof ApiClientError ? err.message : 'Failed to warn account');
    },
  });

  const fangsMutation = useBlockingMutation({
    loadingMessage: 'Modifying Fangs...',
    mutationFn: (data: { amount: number; reason: string }) =>
      accountsApi.modifyFangs(id!, data.amount, data.reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account', id] });
      closeModal(setShowFangsModal);
    },
    onError: (err) => {
      setError(err instanceof ApiClientError ? err.message : 'Failed to modify fangs');
    },
  });

  const deleteAccountMutation = useBlockingMutation({
    loadingMessage: 'Deleting account...',
    mutationFn: () => accountsApi.deleteAccount(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      closeModal(setShowDeleteConfirm);
      navigate('/accounts');
    },
    onError: (err) => {
      setError(err instanceof ApiClientError ? err.message : 'Failed to delete account');
    },
  });

  const updateAccountMutation = useBlockingMutation({
    loadingMessage: 'Updating account...',
    mutationFn: (data: { email?: string; email_verified?: boolean }) =>
      accountsApi.updateAccount(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account', id] });
      closeModal(setShowChangeEmailModal);
    },
    onError: (err) => {
      setError(err instanceof ApiClientError ? err.message : 'Failed to update account');
    },
  });

  const updateUsernameMutation = useBlockingMutation({
    loadingMessage: 'Updating username...',
    mutationFn: (data: { username: string }) =>
      accountsApi.updateAccount(id!, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['account', id] });
      closeModal(setShowChangeUsernameModal);
      // Show warning if email notification failed
      if (response.emailSent === false) {
        const errorMsg = response.emailError || 'Unknown error';
        alert(`Username changed, but notification email failed to send:\n${errorMsg}\n\nMake sure RESEND_API_KEY is configured for the admin worker.`);
      }
    },
    onError: (err) => {
      setError(err instanceof ApiClientError ? err.message : 'Failed to update username');
    },
  });

  if (isLoading) {
    return <div className={styles.loading}>Loading...</div>;
  }

  if (!account) {
    return <div className={styles.error}>Account not found</div>;
  }

  return (
    <div className={styles.container}>
      <Link to="/accounts" className={styles.backLink}>
        &larr; Back to Accounts
      </Link>

      <div className={styles.header}>
        <div className={styles.headerInfo}>
          <h1 className={styles.title}>{account.username_display}</h1>
          <span className={`${styles.status} ${styles[`status${account.status}`]}`}>
            {account.status}
          </span>
        </div>

        {hasRole('mod') && (
          <div className={styles.actions}>
            {account.status === 'banned' ? (
              <Button onClick={() => unbanMutation.mutate()} loading={unbanMutation.isPending}>
                Unban
              </Button>
            ) : (
              <>
                <Button onClick={() => openModal(setShowBanModal)}>Ban</Button>
                <Button onClick={() => openModal(setShowMuteModal)}>Mute</Button>
                <Button onClick={() => openModal(setShowWarnModal)}>Warn</Button>
              </>
            )}
            {hasRole('super') && (
              <>
                <Button onClick={() => openModal(setShowFangsModal)}>Modify Fangs</Button>
                <Button onClick={() => openModal(setShowChangeEmailModal)}>Change Email</Button>
                <Button onClick={() => openModal(setShowChangeUsernameModal)}>Change Username</Button>
                <Button onClick={() => openModal(setShowDeleteConfirm)}>Delete Account</Button>
              </>
            )}
          </div>
        )}
      </div>

      <div className={styles.grid}>
        <div className={styles.card}>
          <h3 className={styles.cardTitle}>Account Info</h3>
          <div className={styles.infoList}>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>ID</span>
              <span className={styles.infoValue}>{account.id}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Email</span>
              <span className={styles.infoValue}>{account.email}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Email Verified</span>
              <span className={styles.infoValue}>
                {account.email_verified ? 'Yes' : 'No'}
                {hasRole('super') && (
                  <button
                    className={styles.toggleBtn}
                    onClick={() =>
                      updateAccountMutation.mutate({
                        email_verified: !account.email_verified,
                      })
                    }
                    disabled={updateAccountMutation.isPending}
                  >
                    Toggle
                  </button>
                )}
              </span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Fangs Balance</span>
              <span className={styles.infoValue}>
                {account.fangs_balance.toLocaleString()}
              </span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Has Purchased</span>
              <span className={styles.infoValue}>
                {account.has_purchased ? 'Yes' : 'No'}
              </span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Discord ID</span>
              <span className={styles.infoValue}>
                {account.discord_id || 'Not linked'}
              </span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Created</span>
              <span className={styles.infoValue}>
                {formatTimestamp(account.created_at)}
              </span>
            </div>
          </div>
        </div>

        <div className={styles.card}>
          <h3 className={styles.cardTitle}>Moderation History</h3>
          {moderationHistory.length > 0 ? (
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeader>Action</TableHeader>
                  <TableHeader>Reason</TableHeader>
                  <TableHeader>By</TableHeader>
                  <TableHeader>Date</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {moderationHistory.map((action) => (
                  <TableRow key={action.id}>
                    <TableCell>
                      <span className={`${styles.actionType} ${styles[`action${action.action_type}`]}`}>
                        {action.action_type}
                      </span>
                    </TableCell>
                    <TableCell>{action.reason}</TableCell>
                    <TableCell>{action.issued_by}</TableCell>
                    <TableCell>
                      {formatDate(action.created_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className={styles.emptyHistory}>No moderation history</p>
          )}
        </div>

        <div className={styles.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #2a2a3e' }}>
            <h3 className={styles.cardTitle} style={{ margin: 0, padding: 0, border: 'none' }}>Bound Devices</h3>
            {hasRole('mod') && devices.length > 0 && (
              <Button onClick={() => openModal(setShowClearDevicesConfirm)}>
                Clear All Devices
              </Button>
            )}
          </div>
          {devicesLoading ? (
            <p className={styles.emptyHistory}>Loading devices...</p>
          ) : devices.length > 0 ? (
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeader>Visitor ID</TableHeader>
                  <TableHeader>Confidence</TableHeader>
                  <TableHeader>First Seen</TableHeader>
                  <TableHeader>Last Seen</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {devices.map((device) => (
                  <TableRow key={device.id}>
                    <TableCell title={device.visitor_id}>
                      {device.visitor_id.substring(0, 12)}...
                    </TableCell>
                    <TableCell>
                      {device.confidence != null ? `${(device.confidence * 100).toFixed(1)}%` : 'N/A'}
                    </TableCell>
                    <TableCell>{formatDate(device.first_seen_at)}</TableCell>
                    <TableCell>{formatDate(device.last_seen_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className={styles.emptyHistory}>No devices bound</p>
          )}
        </div>
      </div>

      {/* Clear Devices Confirmation */}
      {showClearDevicesConfirm && (
        <div className={styles.modalOverlay} onClick={() => closeModal(setShowClearDevicesConfirm)}>
          <Window title="Clear All Devices" maxWidth={400} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <div className={styles.modalForm}>
              <p className={styles.modalInfo}>
                This will remove all device bindings for this account.
                The user will need to re-verify their device on next login.
              </p>
              <div className={styles.modalActions}>
                <Button type="button" onClick={() => closeModal(setShowClearDevicesConfirm)}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={() => clearDevicesMutation.mutate()}
                  loading={clearDevicesMutation.isPending}
                >
                  Confirm Clear
                </Button>
              </div>
            </div>
          </Window>
        </div>
      )}

      {/* Ban Modal */}
      {showBanModal && (
        <ModerationModal
          title="Ban Account"
          onClose={() => closeModal(setShowBanModal)}
          onSubmit={(data) => banMutation.mutate(data)}
          loading={banMutation.isPending}
          error={error}
          showDuration
        />
      )}

      {/* Mute Modal */}
      {showMuteModal && (
        <ModerationModal
          title="Mute Account"
          onClose={() => closeModal(setShowMuteModal)}
          onSubmit={(data) => muteMutation.mutate({ ...data, duration_minutes: data.duration_minutes! })}
          loading={muteMutation.isPending}
          error={error}
          showDuration
          requireDuration
        />
      )}

      {/* Warn Modal */}
      {showWarnModal && (
        <ModerationModal
          title="Warn Account"
          onClose={() => closeModal(setShowWarnModal)}
          onSubmit={(data) => warnMutation.mutate({ reason: data.reason })}
          loading={warnMutation.isPending}
          error={error}
        />
      )}

      {/* Fangs Modal */}
      {showFangsModal && (
        <FangsModal
          onClose={() => closeModal(setShowFangsModal)}
          onSubmit={(data) => fangsMutation.mutate(data)}
          loading={fangsMutation.isPending}
          error={error}
          currentBalance={account.fangs_balance}
        />
      )}

      {/* Delete Account Confirmation */}
      {showDeleteConfirm && (
        <div className={styles.modalOverlay} onClick={() => closeModal(setShowDeleteConfirm)}>
          <Window title="Delete Account" maxWidth={400} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <div className={styles.modalForm}>
              <p className={styles.modalInfo}>
                This will <strong>permanently delete</strong> the account
                <strong> {account.username_display}</strong> ({account.email}) and all associated data.
                This action cannot be undone.
              </p>
              {error && <div className={styles.modalError}>{error}</div>}
              <div className={styles.modalActions}>
                <Button type="button" onClick={() => closeModal(setShowDeleteConfirm)}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={() => deleteAccountMutation.mutate()}
                  loading={deleteAccountMutation.isPending}
                >
                  Delete Permanently
                </Button>
              </div>
            </div>
          </Window>
        </div>
      )}

      {/* Change Email Modal */}
      {showChangeEmailModal && (
        <ChangeEmailModal
          onClose={() => closeModal(setShowChangeEmailModal)}
          onSubmit={(email) => updateAccountMutation.mutate({ email })}
          loading={updateAccountMutation.isPending}
          error={error}
          currentEmail={account.email}
        />
      )}

      {/* Change Username Modal */}
      {showChangeUsernameModal && (
        <ChangeUsernameModal
          onClose={() => closeModal(setShowChangeUsernameModal)}
          onSubmit={(username) => updateUsernameMutation.mutate({ username })}
          loading={updateUsernameMutation.isPending}
          error={error}
          currentUsername={account.username_display}
          accountId={account.id}
        />
      )}
    </div>
  );
}

interface ModerationModalProps {
  title: string;
  onClose: () => void;
  onSubmit: (data: { reason: string; duration_minutes?: number }) => void;
  loading: boolean;
  error: string;
  showDuration?: boolean;
  requireDuration?: boolean;
}

function ModerationModal({
  title,
  onClose,
  onSubmit,
  loading,
  error,
  showDuration,
  requireDuration,
}: ModerationModalProps) {
  const [reason, setReason] = useState('');
  const [duration, setDuration] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      reason,
      duration_minutes: duration ? parseInt(duration, 10) : undefined,
    });
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <Window title={title} maxWidth={400} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <form onSubmit={handleSubmit} className={styles.modalForm}>
          {error && <div className={styles.modalError}>{error}</div>}

          <TextField
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
          />

          {showDuration && (
            <TextField
              label={`Duration (minutes)${requireDuration ? '' : ' - leave empty for permanent'}`}
              type="number"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              required={requireDuration}
              min={1}
              max={525600}
            />
          )}

          <div className={styles.modalActions}>
            <Button type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={loading}>
              Confirm
            </Button>
          </div>
        </form>
      </Window>
    </div>
  );
}

interface FangsModalProps {
  onClose: () => void;
  onSubmit: (data: { amount: number; reason: string }) => void;
  loading: boolean;
  error: string;
  currentBalance: number;
}

interface ChangeEmailModalProps {
  onClose: () => void;
  onSubmit: (email: string) => void;
  loading: boolean;
  error: string;
  currentEmail: string;
}

function ChangeEmailModal({ onClose, onSubmit, loading, error, currentEmail }: ChangeEmailModalProps) {
  const [email, setEmail] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(email);
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <Window title="Change Email" maxWidth={400} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <form onSubmit={handleSubmit} className={styles.modalForm}>
          {error && <div className={styles.modalError}>{error}</div>}

          <p className={styles.modalInfo}>
            Current email: <strong>{currentEmail}</strong>
          </p>
          <p className={styles.modalInfo}>
            Changing email will reset verification status.
          </p>

          <TextField
            label="New Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />

          <div className={styles.modalActions}>
            <Button type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={loading}>
              Change Email
            </Button>
          </div>
        </form>
      </Window>
    </div>
  );
}

interface ChangeUsernameModalProps {
  onClose: () => void;
  onSubmit: (username: string) => void;
  loading: boolean;
  error: string;
  currentUsername: string;
  accountId: string;
}

function ChangeUsernameModal({ onClose, onSubmit, loading, error, currentUsername, accountId }: ChangeUsernameModalProps) {
  const [username, setUsername] = useState('');
  const [validationStatus, setValidationStatus] = useState<'idle' | 'checking' | 'valid' | 'invalid'>('idle');
  const [validationError, setValidationError] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Client-side validation rules
  const validateUsernameFormat = (value: string): { valid: boolean; error?: string } => {
    const trimmed = value.trim();
    if (trimmed.length === 0) {
      return { valid: false };
    }
    if (trimmed.length < 3) {
      return { valid: false, error: 'Minimum 3 characters' };
    }
    if (trimmed.length > 16) {
      return { valid: false, error: 'Maximum 16 characters' };
    }
    if (!/^[a-zA-Z0-9]+$/.test(trimmed)) {
      return { valid: false, error: 'Only letters and numbers allowed' };
    }
    return { valid: true };
  };

  useEffect(() => {
    // Clear any existing debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    const trimmed = username.trim();

    if (trimmed.length === 0) {
      setValidationStatus('idle');
      setValidationError('');
      return;
    }

    // Client-side validation first
    const formatValidation = validateUsernameFormat(trimmed);
    if (!formatValidation.valid) {
      setValidationStatus('invalid');
      setValidationError(formatValidation.error || '');
      return;
    }

    // Same as current (case-insensitive)
    if (trimmed.toLowerCase() === currentUsername.toLowerCase()) {
      setValidationStatus('invalid');
      setValidationError('Same as current username');
      return;
    }

    // Debounced server-side availability check
    setValidationStatus('checking');
    setValidationError('');

    debounceRef.current = setTimeout(async () => {
      try {
        const result = await accountsApi.checkUsername(trimmed, accountId);
        if (result.available) {
          setValidationStatus('valid');
          setValidationError('');
        } else {
          setValidationStatus('invalid');
          setValidationError(result.error || 'Username not available');
        }
      } catch {
        setValidationStatus('invalid');
        setValidationError('Failed to check availability');
      }
    }, 500);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [username, currentUsername, accountId]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validationStatus === 'valid') {
      onSubmit(username.trim());
    }
  };

  const canSubmit = validationStatus === 'valid' && !loading;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <Window title="Change Username" maxWidth={400} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <form onSubmit={handleSubmit} className={styles.modalForm}>
          {error && <div className={styles.modalError}>{error}</div>}

          <p className={styles.modalInfo}>
            Current username: <strong>{currentUsername}</strong>
          </p>

          <TextField
            label="New Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
          />

          {validationStatus === 'checking' && (
            <div className={styles.validationMessage}>Checking availability...</div>
          )}
          {validationStatus === 'valid' && (
            <div className={`${styles.validationMessage} ${styles.validationValid}`}>Username available</div>
          )}
          {validationStatus === 'invalid' && validationError && (
            <div className={`${styles.validationMessage} ${styles.validationInvalid}`}>{validationError}</div>
          )}

          <div className={styles.modalActions}>
            <Button type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={loading} disabled={!canSubmit}>
              Change Username
            </Button>
          </div>
        </form>
      </Window>
    </div>
  );
}

function FangsModal({ onClose, onSubmit, loading, error, currentBalance }: FangsModalProps) {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({ amount: parseInt(amount, 10), reason });
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <Window title="Modify Fangs" maxWidth={400} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <form onSubmit={handleSubmit} className={styles.modalForm}>
          {error && <div className={styles.modalError}>{error}</div>}

          <p className={styles.modalInfo}>
            Current balance: <strong>{currentBalance.toLocaleString()}</strong> Fangs
          </p>

          <TextField
            label="Amount (positive to add, negative to remove)"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
          />

          <TextField
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
          />

          <div className={styles.modalActions}>
            <Button type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={loading}>
              Confirm
            </Button>
          </div>
        </form>
      </Window>
    </div>
  );
}
